import polygonClipping from "polygon-clipping";
import type { Geometry } from "geojson";

/**
 * Tidy a Polygon / MultiPolygon so Mapbox GL can draw it without artifacts.
 *
 * John Deere boundary imports can contain interior rings (exclusion zones) that
 * touch or cross the exterior ring. Those are OGC-invalid, and Mapbox GL's
 * earcut tessellation renders them with slivers, seams or filled "holes".
 * OpenLayers (the Fields screen) fills them tolerantly so they look fine there,
 * but the Mapbox-based Tracks map does not — hence the artifacts.
 *
 * A self-union resolves the ring intersections while preserving valid holes.
 *
 * DISPLAY-ONLY: this returns a NEW geometry meant for rendering. The geometry
 * stored on the server is never modified — callers must not persist the result
 * or use it for area / measurement. Mirrors the backend `repairGeometry()`
 * (sentinelHubService.ts), which does the same self-union for the copy sent to
 * Sentinel Hub, so the Tracks map now matches the NDVI footprint.
 *
 * Fails closed: non-polygon input, an empty union result, or any thrown error
 * all return the original geometry unchanged — worst case is today's behavior,
 * never a crash.
 */
export function repairGeometryForDisplay<T extends Geometry | null | undefined>(
  geometry: T
): T {
  if (!geometry || (geometry.type !== "Polygon" && geometry.type !== "MultiPolygon")) {
    return geometry;
  }

  try {
    // polygon-clipping expects MultiPolygon-shaped coordinates. Wrap a single
    // Polygon's rings into a one-polygon MultiPolygon; pass a MultiPolygon's
    // coordinates through as-is.
    const multi =
      geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates;

    const result = polygonClipping.union(multi as any);
    if (!result || result.length === 0) return geometry;

    const repaired: Geometry =
      result.length === 1
        ? { type: "Polygon", coordinates: result[0] as any }
        : { type: "MultiPolygon", coordinates: result as any };

    return repaired as T;
  } catch {
    return geometry;
  }
}
